'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { WebsiteSettingsContext } from '../context/WebsiteSettingsContext';
// eslint-disable-next-line import/no-cycle
import { useGoogleTagManager, useFacebookPixel } from './hooks';

export const AnalyticsContext = createContext({
  sendPageView: () => {},
  fbtrack: () => {},
  gtagEvent: () => {},
  gtagEcommerceViewItem: () => {},
  gtagEcommerceBeginCheckout: () => {},
  gtagEcommercePurchase: () => {},
  gtagEcommerceGroupReservationsPurchase: () => {},
  reservationTypes: () => {},
});

export const AnalyticsProvider = ({ children }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const queryString = searchParams.toString();
  const searchString = queryString ? `?${queryString}` : '';
  const currentUrl = pathname + searchString;

  const initializationStatuses = useRef({
    isGTAGInit: false,
    isFBInit: false,
  });
  const {
    current: { isGTAGInit, isFBInit },
  } = initializationStatuses;

  const prevPath = useRef(currentUrl);

  const {
    analytics: { googleAnalytics: gaId = null, googleTag: tagId = null, facebookPixel: facebookPixelId = null } = {},
    cookiesPolicyAccepted,
  } = useContext(WebsiteSettingsContext);

  const {
    initGoogleTagManager,
    gtagEvent,
    gtagEcommerceViewItem,
    gtagEcommerceBeginCheckout,
    gtagEcommercePurchase,
    gtagEcommerceGroupReservationsPurchase,
    reservationTypes,
    gtagPageViewEvent,
  } = useGoogleTagManager();
  const { initAnalytics: initFacebookAnalytics, fbTrackPageView, fbtrack } = useFacebookPixel();

  const shouldShowCookiePolicy = useMemo(() => {
    return !!facebookPixelId;
  }, [facebookPixelId]);

  const sendPageView = useCallback(
    (url) => {
      const URL = url || currentUrl;
      gtagPageViewEvent();
      fbTrackPageView(URL);
      prevPath.current = currentUrl;
    },
    [currentUrl, fbTrackPageView, gtagPageViewEvent]
  );

  const value = useMemo(() => {
    return {
      sendPageView,
      fbtrack,
      gtagEvent,
      gtagEcommerceViewItem,
      gtagEcommerceBeginCheckout,
      gtagEcommercePurchase,
      gtagEcommerceGroupReservationsPurchase,
      reservationTypes,
      shouldShowCookiePolicy,
    };
  }, [
    fbtrack,
    gtagEcommerceBeginCheckout,
    gtagEcommerceGroupReservationsPurchase,
    gtagEcommercePurchase,
    gtagEcommerceViewItem,
    gtagEvent,
    shouldShowCookiePolicy,
    reservationTypes,
    sendPageView,
  ]);

  useEffect(() => {
    if ((tagId || gaId) && !isGTAGInit) {
      initGoogleTagManager({
        tagId,
        gaId,
      });
      initializationStatuses.current.isGTAGInit = true;
      gtagPageViewEvent(currentUrl);
    }
    if (facebookPixelId && !isFBInit && cookiesPolicyAccepted) {
      initFacebookAnalytics(facebookPixelId);
      initializationStatuses.current.isFBInit = true;
      fbTrackPageView(currentUrl);
    }
  }, [
    facebookPixelId,
    initGoogleTagManager,
    initFacebookAnalytics,
    sendPageView,
    fbTrackPageView,
    isGTAGInit,
    isFBInit,
    currentUrl,
    cookiesPolicyAccepted,
    tagId,
    gaId,
    gtagPageViewEvent,
  ]);

  useEffect(() => {
    if (currentUrl !== prevPath.current) {
      sendPageView(currentUrl);
      prevPath.current = currentUrl;
    }
  }, [currentUrl, sendPageView]);

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};
